// #6 Relatório Final de Apuração (REL-CMP-001) — agrega o caso INTEIRO.
// Como o Plano/Termo, FORA do registry: recebe RelatorioData com TODO o conteúdo
// já DECIFRADO pelo chamador (uma única abertura de DEK: atas + justificativas de
// dispensa + justificativas de parecer + fundamentação da decisão).
//
// Split auto/decifrado: o DISPOSITIVO (kind, tally, datas, relator, prazo) e a
// triagem vêm do `state` (metadado, via getDecisao/getTriagem); o TEXTO (atas,
// pareceres, fundamentação) vem decifrado em RelatorioData. A carta ao denunciante
// é EXCLUÍDA por decisão (peça de notificação, não de relatório interno).

import type { FormBlock } from './form-pdf.js';
import { manualField } from './form-pdf.js';
import {
  type FormInput,
  categoriaLabel,
  competenciaLabel,
  decisaoLabel,
  fmtDate,
  gravidadeLabel,
  rotaLabel,
  memberLabel,
  getOpenedAt,
  getTriagem,
  getDecisao,
} from './forms-common.js';

/** Uma oitiva/dispensa já decifrada (conteúdo + metadado). */
export interface RelatorioOitiva {
  tipo: 'oitiva' | 'dispensa';
  /** registeredAt (oitiva) / autorizadoEm (dispensa). */
  data: number;
  /** registeredBy (oitiva) / autorizadoPor (dispensa) — rotulado via input.members. */
  porUlid: string;
  /** ata (oitiva) ou justificativa (dispensa), decifrada. Vazio → manual. */
  conteudo: string;
  /** presentes decifrados — só oitiva. */
  presentes?: string;
}

/** Um parecer já decifrado. */
export interface RelatorioParecer {
  memberId: string;
  voto: 'procedente' | 'improcedente' | 'improcedente_insuficiencia';
  /** justificativa decifrada. Vazio → manual. */
  justificativa: string;
}

/** Tudo o que o chamador decifrou numa abertura de DEK (o resto vem do state). */
export interface RelatorioData {
  oitivas: ReadonlyArray<RelatorioOitiva>;
  pareceres: ReadonlyArray<RelatorioParecer>;
  /** fundamentação da decisão (decisao.justificativaCiphertext) decifrada. */
  fundamentacao: string;
}

export function buildRelatorio(input: FormInput, rel: RelatorioData): FormBlock[] {
  const triagem = getTriagem(input.state);
  const decisao = getDecisao(input.state);
  const openedAt = getOpenedAt(input.state);

  const blocks: FormBlock[] = [
    { kind: 'title', text: 'Relatório Final de Apuração' },
    { kind: 'field', label: 'Código:', value: 'REL-CMP-001' },

    // 1. Identificação — metadado.
    { kind: 'section', text: '1. Identificação' },
    { kind: 'field', label: 'Processo (protocolo):', value: input.caseId },
    { kind: 'field', label: 'Categoria:', value: categoriaLabel(input.categoria) },
    { kind: 'field', label: 'Comitê de competência:', value: competenciaLabel(input.categoria) },
    { kind: 'field', label: 'Aberto em:', value: openedAt === null ? '' : fmtDate(openedAt) },
    {
      kind: 'field',
      label: 'Decisão em:',
      value: decisao ? fmtDate(decisao.emittedAt) : '',
    },

    // 2. Triagem — metadado.
    { kind: 'section', text: '2. Triagem' },
    {
      kind: 'field',
      label: 'Gravidade:',
      value: triagem ? gravidadeLabel(triagem.gravidade) : '',
    },
    { kind: 'field', label: 'Rota:', value: triagem ? rotaLabel(triagem.rota) : '' },

    // 3. Instrução — oitivas + dispensas (conteúdo DECIFRADO).
    { kind: 'section', text: '3. Instrução — oitivas e dispensas' },
  ];

  if (rel.oitivas.length === 0) {
    blocks.push({ kind: 'paragraph', text: 'Nenhuma oitiva ou dispensa registrada.' });
  } else {
    for (const o of rel.oitivas) {
      const rotulo = o.tipo === 'oitiva' ? 'Oitiva' : 'Dispensa de oitiva';
      blocks.push({
        kind: 'field',
        label: `${rotulo} (${fmtDate(o.data)}):`,
        value: `registrado por ${memberLabel(input.members, o.porUlid)}`,
      });
      if (o.tipo === 'oitiva') {
        blocks.push({
          kind: 'field',
          label: 'Presentes:',
          value: o.presentes && o.presentes.trim() !== '' ? o.presentes : '',
        });
      }
      if (o.conteudo.trim() === '') {
        blocks.push(manualField(o.tipo === 'oitiva' ? 'Ata:' : 'Justificativa:'));
      } else {
        blocks.push({ kind: 'paragraph', text: o.conteudo });
      }
    }
  }

  // 4. Pareceres do comitê (justificativas DECIFRADAS).
  blocks.push({ kind: 'section', text: '4. Pareceres do comitê' });
  if (rel.pareceres.length === 0) {
    blocks.push({ kind: 'paragraph', text: 'Nenhum parecer emitido.' });
  } else {
    for (const p of rel.pareceres) {
      blocks.push({
        kind: 'field',
        label: `${memberLabel(input.members, p.memberId)} — voto:`,
        value: decisaoLabel(p.voto),
      });
      if (p.justificativa.trim() === '') {
        blocks.push(manualField('Justificativa:'));
      } else {
        blocks.push({ kind: 'paragraph', text: p.justificativa });
      }
    }
  }

  // 5. Decisão — dispositivo (metadado) + fundamentação (DECIFRADA).
  blocks.push({ kind: 'section', text: '5. Decisão' });
  if (decisao) {
    blocks.push(
      { kind: 'field', label: 'Resultado:', value: decisaoLabel(decisao.kind) },
      {
        kind: 'table',
        headers: ['Voto', 'Quantidade'],
        rows: [
          ['Procedente', String(decisao.votoTally.procedente)],
          ['Improcedente', String(decisao.votoTally.improcedente)],
          ['Imp. por insuficiência', String(decisao.votoTally.improcedente_insuficiencia)],
        ],
      },
      { kind: 'field', label: 'Relator:', value: memberLabel(input.members, decisao.emittedBy) },
      {
        kind: 'field',
        label: 'Prazo recursal até:',
        value: fmtDate(decisao.prazoRecursalAteEpochMs),
      },
    );
  }
  if (rel.fundamentacao.trim() === '') {
    blocks.push(manualField('Fundamentação da decisão:'));
  } else {
    blocks.push({ kind: 'paragraph', text: rel.fundamentacao });
  }

  // 6. Encerramento — assinatura do relator.
  blocks.push(
    { kind: 'section', text: '6. Encerramento' },
    { kind: 'spacer', mm: 6 },
    { kind: 'signature', nome: '', papel: 'Relator / Comitê de apuração' },
  );

  return blocks;
}
